/**
 * 화면 사용로그 · 에러로그 — **보내는 곳은 이 파일 하나.**
 *
 * 백엔드 라우트는 서버 미들웨어가 자동으로 잡는다. 여기는 **서버가 아예 모르는 것**을 보낸다:
 * 편집기 조작, 모달 열기, 설정 변경 같은 화면에서 끝나는 기능. "사용자가 어떤 기능을 쓰는가"
 * 를 보려면 오히려 그쪽이 본체다.
 *
 * ## 지켜야 할 것 셋
 *
 * 1. **던지지 않는다.** 로그를 남기려다 화면이 멈추는 것은 앞뒤가 바뀐 일이다. 모든 경로가
 *    try 로 감싸여 있고, 전송 실패도 삼킨다(재시도하지 않는다 — 실패한 로그를 붙들고 있다가
 *    다음 배치까지 못 보내는 편이 더 나쁘다).
 * 2. **막지 않는다.** 액션마다 요청을 보내면 클릭 한 번에 왕복이 하나씩 붙는다. 버퍼에
 *    모았다가 주기적으로 한 번에 보낸다.
 * 3. **떠날 때 잃지 않는다.** 탭을 닫거나 새로고침하면 버퍼가 통째로 사라진다 — 그런데 마지막
 *    몇 건이 대개 가장 궁금한 것이다(무엇을 하다 나갔나). `sendBeacon` 으로 넘긴다.
 *
 * ## 액션 id 는 목록에 있는 것만
 *
 * 여기 목록은 **오타를 줄이는 용도**이고 판정은 서버가 한다(브라우저에서 오는 것은 믿지
 * 않는다). 그래도 여기서 한 번 걸러 보내면 쓸데없는 왕복이 줄고, 개발 중에 바로 눈에 띈다.
 * 서버 목록(`backend/modules/opsLog/clientActions.js`)과 어긋나지 않는지는 테스트가 지킨다.
 */

import { observe } from '../utils/agentObserver';
import buildInfo from '../build-info.json';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '/sql-developer';
const ENDPOINT = `${BASE_URL}/logs/client`;

/** 빌드 식별자 — 서버가 이 값으로 소스맵을 찾는다(`scripts/stash-sourcemaps.js` 가 같은 값으로 보관한다). */
const RELEASE = (buildInfo && buildInfo.release) || null;

/**
 * 브라우저·OS 를 **이름과 주 버전만.** UA 전문은 사람을 구별하는 지문이 되고, 조치에 필요한 것은
 * "Safari 에서만 난다" 정도다. 순서가 뜻이 있다 — Edge·Whale·삼성 인터넷은 UA 에 Chrome 도 적는다.
 */
export function describeBrowser(ua) {
    const s = String(ua || '');
    const pick = (re, name) => { const m = re.exec(s); return m ? `${name} ${m[1]}` : null; };
    const electron = pick(/Electron\/(\d+)/, 'Electron');
    const browser = electron
        || pick(/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge')
        || pick(/OPR\/(\d+)/, 'Opera')
        || pick(/Whale\/(\d+)/, 'Whale')
        || pick(/SamsungBrowser\/(\d+)/, 'Samsung')
        || pick(/(?:Firefox|FxiOS)\/(\d+)/, 'Firefox')
        || pick(/(?:Chrome|CriOS)\/(\d+)/, 'Chrome')
        || (/Safari\//.test(s) ? pick(/Version\/(\d+)/, 'Safari') : null)
        || 'Other';
    let os = 'Other';
    if (/Windows/.test(s)) os = 'Windows';
    else if (/Android/.test(s)) os = 'Android';
    else if (/iPhone|iPad|iPod/.test(s)) os = 'iOS';
    else if (/Mac OS X/.test(s)) os = 'macOS';
    else if (/Linux/.test(s)) os = 'Linux';
    return `${browser} / ${os}`;
}

/** 오류마다 싣는 맥락. 서버가 모양을 다시 확인한다(경로는 쿼리·id 를 버린다). */
function errorContext() {
    try {
        return {
            release: RELEASE,
            route: typeof window !== 'undefined' ? window.location.pathname : null,
            browser: typeof navigator !== 'undefined' ? describeBrowser(navigator.userAgent) : null,
        };
    } catch (_e) { return {}; }
}

/** 서버 화이트리스트와 **같은 목록**. 어긋나면 테스트가 잡는다. */
export const CLIENT_ACTIONS = [
    // 편집기
    'editor.newFile', 'editor.openFile', 'editor.saveFile', 'editor.closeTab',
    'editor.format', 'editor.bindPrompt', 'editor.snippet',
    // 탐색기
    'explorer.expandSchema', 'explorer.tableInfo', 'explorer.search',
    // 결과 그리드
    'grid.sort', 'grid.filter', 'grid.copy', 'grid.columnSelect',
    // 창 열기
    'modal.monitoringDashboard', 'modal.monitoringAnalysis', 'modal.dbmsPerf',
    'modal.resourceAnalysis', 'modal.sqlPerf', 'modal.sessionAdmin', 'modal.dynamicView',
    'modal.planArchive',
    'modal.sqlTuning',
    'modal.sqlReview',
    'modal.sqlAuthor',
    'modal.sqlQuality',
    'modal.schemaCompare',
    'modal.sqlCollect',
    'modal.sqlBuilder',
    'modal.dataAnalysis',
    'modal.tuningCases', 'modal.tableAnalysis', 'modal.indexDesign', 'modal.partitionDesign', 'modal.designGuides',
    'modal.topicManager', 'modal.guideManager',
    'modal.settings', 'modal.help', 'modal.dataTransfer', 'modal.challenge', 'modal.feedback',
    // AI 에이전트
    'assistant.openPanel', 'assistant.closePanel', 'assistant.newThread',
    'assistant.skillLevel', 'assistant.tuningLevel', 'assistant.attach',
    'assistant.insertSql', 'assistant.runSuggested',
    // 도움말
    'help.quickStart', 'help.topic',
    // 설정
    'pref.theme', 'pref.language', 'pref.zoom', 'pref.keyBindings', 'pref.favorite',
];

const KNOWN = new Set(CLIENT_ACTIONS);

/**
 * **직전에 무엇을 했나** — 문제 신고에 함께 싣는다(화이트리스트 id 와 시각만). 사용자가 재현 절차를
 * 적지 않아도 흐름이 보인다. 메모리에만 둔다 — 새로고침하면 사라지는 것이 맞다.
 */
const RECENT_ACTIONS_MAX = 30;
const recentActions = [];
/** 이 창에서 난 오류 — 신고 창이 "이것을 함께 보낼까요" 로 보여 준다. */
const RECENT_ERRORS_MAX = 10;
const recentErrors = [];

export const getRecentActions = () => recentActions.slice();
export const getRecentErrors = () => recentErrors.slice();

/**
 * 오류 번호 — 사용자에게 보여 주고 기록에도 싣는다. 사용자는 번호만 알려 주면 되고, 신고와 기록이 이것으로
 * 이어진다. 읽어 줄 때 헷갈리는 0/O·1/I/L 을 뺐다. 서버의 모양 검사(`E-[A-Z0-9]{4,10}`)와 맞는다.
 */
const INCIDENT_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export function newIncidentId() {
    const n = 6;
    let out = '';
    try {
        const bytes = new Uint8Array(n);
        window.crypto.getRandomValues(bytes);
        for (const b of bytes) out += INCIDENT_ALPHABET[b % INCIDENT_ALPHABET.length];
    } catch (_e) {
        out = '';
        for (let i = 0; i < n; i++) out += INCIDENT_ALPHABET[Math.floor(Math.random() * INCIDENT_ALPHABET.length)];
    }
    return `E-${out}`;
}

/** 모아 두는 상한·주기. 짧으면 왕복이 잦고 길면 떠날 때 더 많이 잃는다. */
const FLUSH_MS = 10000;
const MAX_BUFFER = 50;

let buffer = [];
let timer = null;
let armed = false;

function send(items, { beacon = false } = {}) {
    if (!items.length) return;
    const payload = JSON.stringify({ items });
    try {
        if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            // 떠나는 중이라 fetch 는 취소된다. Blob 타입을 text/plain 으로 둬야 프리플라이트가
            // 없어 그대로 나간다 — 서버가 문자열 본문도 파싱한다.
            navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain' }));
            return;
        }
        fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: payload,
            keepalive: true,
        }).catch(() => { /* 전송 실패는 삼킨다 — 재시도하면 다음 배치까지 못 보낸다 */ });
    } catch (_e) { /* 로그가 화면을 막으면 안 된다 */ }
}

export function flush({ beacon = false } = {}) {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!buffer.length) return 0;
    const items = buffer;
    buffer = [];
    send(items, { beacon });
    return items.length;
}

function arm() {
    if (timer) return;
    timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
}

/** 탭을 닫거나 숨길 때 남은 것을 넘긴다. 마지막 몇 건이 대개 가장 궁금한 것이다. */
function armUnload() {
    if (armed || typeof window === 'undefined') return;
    armed = true;
    // `pagehide` 가 `beforeunload` 보다 믿을 만하다(모바일·bfcache 에서 안 뜨는 경우가 있다)
    window.addEventListener('pagehide', () => flush({ beacon: true }));
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush({ beacon: true });
    });
}

/**
 * **화면 액션을 기록한다.** 이 함수 말고 다른 경로로 보내지 말 것 —
 * 두 곳에서 보내면 버퍼가 갈려 한쪽이 떠날 때 통째로 사라진다.
 *
 * @param action  `CLIENT_ACTIONS` 에 있는 id
 * @param detail  선택. 서버가 화이트리스트로 다시 거른다(값은 안 담긴다)
 */
export function logAction(action, detail = null) {
    try {
        if (!KNOWN.has(action)) {
            // 개발 중에 바로 눈에 띄게. 서버도 버리지만 여기서 알려 줘야 고친다.
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('[opsLog] 목록에 없는 액션 id:', action);
            }
            return;
        }
        armUnload();
        recentActions.push({ action, at: new Date().toISOString() });
        if (recentActions.length > RECENT_ACTIONS_MAX) recentActions.shift();
        buffer.push({ action, ...(detail || {}) });
        if (buffer.length >= MAX_BUFFER) flush();
        else arm();
    } catch (_e) { /* 절대 던지지 않는다 */ }
}

/**
 * 화면에서 난 오류. 서버 요청 실패는 서버가 이미 남기므로 **화면에서만 나는 것**을 보낸다 —
 * 렌더 실패, 미처리 거부, 액션 처리 중 예외.
 *
 * @param kind 'render' | 'unhandled' | 'network' | 'action'
 */
export function logClientError(kind, err, extra = null) {
    // 번호는 기록이 실패해도 돌려준다 — 화면은 번호를 보여 주고, 사용자는 그것으로 신고한다
    const incidentId = newIncidentId();
    try {
        armUnload();
        const errorType = (err && (err.errorType || err.name)) || 'CLIENT_ERROR';
        buffer.push({
            kind: 'error',
            errorKind: kind,
            errorType,
            message: err && err.message ? String(err.message) : String(err || ''),
            stack: err && err.stack ? String(err.stack) : undefined,
            incidentId,
            ...errorContext(),
            ...(extra || {}),
        });
        recentErrors.push({ id: incidentId, at: Date.now(), errorType, errorKind: kind });
        // 에이전트의 상시 관찰에도 남긴다(utils/agentObserver) — 화면 오류는 앱 어디서 났든 이 길을 지난다
        observe({ kind: 'appError', severity: 'error', title: `${errorType}: ${(err && err.message) || String(err || '')}`.slice(0, 160),
            detail: err && err.message ? String(err.message) : String(err || ''), source: kind, key: `appError:${errorType}:${(err && err.message) || ''}` });
        if (recentErrors.length > RECENT_ERRORS_MAX) recentErrors.shift();
        // 오류는 기다리지 않는다 — 이 뒤에 화면이 죽을 수도 있다
        flush();
    } catch (_e) { /* 절대 던지지 않는다 */ }
    return incidentId;
}

// ── 화면 오류 자동 수집 ────────────────────────────────────

/**
 * **기록하지 않을 잡음.** `App.js` 의 `KNOWN_BENIGN` 과 **일부러 별도로 둔다**(사용자 결정
 * 2026-09-11).
 *
 * 그 목록은 "개발 중 빨간 오류창을 띄우지 말 것" 용도다. 판단이 다르다 — 창을 숨길지와
 * 기록할 가치가 있는지는 같은 질문이 아니다. 한 목록을 나눠 쓰면 나중에 누가 "이 오류도 창
 * 띄우지 말자" 고 항목을 더했을 때 **기록도 같이 조용히 멈춘다.** 이 저장소가 반복해서 다친
 * 모양이라(한 값이 두 판단에 쓰이다 한쪽이 따라 움직임) 처음부터 갈라 둔다.
 *
 * 지금은 같은 두 항목으로 시작한다. 늘릴 때는 **왜 기록할 가치가 없는지**를 적을 것.
 */
const NOISE = [
    // 브라우저가 창 크기 계산 중에 내는 경고. 동작에 영향이 없고 초당 여러 번 난다.
    'ResizeObserver loop completed with undelivered notifications',
    'ResizeObserver loop limit exceeded',
];

const isNoise = (msg) => {
    const m = String(msg || '');
    return !m || NOISE.some(p => m.includes(p));
};

let installed = false;

/**
 * 창 전체의 오류를 기록에 잇는다. **index.js 에서 한 번만** 부른다.
 *
 * 캡처 단계로 등록해 `App.js` 의 억제기(같은 캡처 단계, 나중에 등록)보다 **먼저** 돈다 —
 * 그쪽이 잡음에 `stopImmediatePropagation` 을 걸기 때문에, 뒤에 붙으면 우리가 아예 못 본다.
 * 먼저 보되 **우리 목록으로 판단**한다.
 *
 * 우리는 이벤트를 막지 않는다(`preventDefault` 도 `stop*` 도 하지 않는다) — 기록은 관찰이지
 * 개입이 아니다. 억제 여부는 그대로 App.js 가 정한다.
 */
export function installGlobalErrorLogging() {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    try {
        window.addEventListener('error', (e) => {
            try {
                if (isNoise(e && e.message)) return;
                // 교차 출처 스크립트 등은 e.error 없이 파일·줄만 준다 — 그것을 프레임 한 줄로 만들어 위치를 살린다
                const frame = e && e.filename ? `    at ${e.filename}:${e.lineno || 0}:${e.colno || 0}` : undefined;
                const error = e && e.error && e.error.stack ? e.error : { message: e && e.message, stack: frame };
                logClientError('unhandled', error, {
                    // 어디서 났는지 — 파일·줄은 값이 아니라 위치다
                    message: e && e.message,
                });
            } catch (_x) { /* 기록이 화면을 막으면 안 된다 */ }
        }, true);

        window.addEventListener('unhandledrejection', (e) => {
            try {
                const reason = e && e.reason;
                if (isNoise(reason && reason.message ? reason.message : reason)) return;
                // 서버 요청 실패는 **서버가 이미 남긴다** — 여기서 또 남기면 두 번 세어진다
                if (reason && reason.body && reason.errorType) return;
                logClientError('unhandled', reason);
            } catch (_x) { /* 같은 이유 */ }
        }, true);
    } catch (_e) { /* 절대 던지지 않는다 */ }
}

// ── 조회 (열람 화면이 쓴다) ────────────────────────────────
//
// **보내는 것과 읽는 것을 한 파일에 둔다.** 엔드포인트가 같은 곳이고, 나뉘어 있으면 경로가
// 두 벌이 되어 한쪽만 바뀌는 날이 온다.

async function get(path, params = {}) {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
    }
    const qs = sp.toString();
    const r = await fetch(`${BASE_URL}/logs${path}${qs ? `?${qs}` : ''}`, { credentials: 'include' });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
        // 문구를 여기서 만들지 않는다 — 서비스 모듈은 t() 를 못 쓴다. 서버가 준 메시지를
        // 그대로 쓰고, 없으면 **상태 코드**만 남긴다(ORA 번호처럼 번역하지 않는 판별용 값이다).
        const err = new Error(body.error || `HTTP ${r.status}`);
        err.errorType = body.errorType || 'REQUEST_FAILED';
        err.body = body;
        throw err;
    }
    return body;
}

export const fetchUseLogs = (p) => get('/use', p);
export const fetchErrorLogs = (p) => get('/errors', p);
export const fetchLogSummary = (p) => get('/summary', p);
export const fetchLogLevel = () => get('/level');
export const fetchFeedback = (p) => get('/feedback', p);
export const fetchLogGroups = (p) => get('/groups', p);
export const fetchUnseen = () => get('/unseen');
/** 내 신고에 새 답이 몇 건인가(본인 것만) */
export const fetchFeedbackUnseen = () => get('/feedback/unseen');
export const FEEDBACK_SEEN_EVENT = 'livesql:feedback-seen';

async function putJson(path, body) {
    const r = await fetch(`${BASE_URL}/logs${path}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body || {}),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
        const e = new Error(data.error || `HTTP ${r.status}`);
        e.errorType = data.errorType || 'REQUEST_FAILED';
        e.body = data;
        throw e;
    }
    return data;
}

/** 오류 묶음의 해결 표시(`resolved`)·되돌리기(`open`). 관리자만 — 서버가 판정한다. */
export const setGroupStatus = (fingerprint, status) => putJson(`/groups/${encodeURIComponent(fingerprint)}`, { status });

/** 로그 창을 열었다고 알린다 — 메뉴의 알림 표시가 다음 폴링을 기다리지 않고 사라지게 이벤트도 보낸다. */
/** "내 신고" 를 열었다 — 새 답 표시를 지운다 */
export async function markFeedbackSeen() {
    try {
        await putJson('/feedback/seen', {});
        if (typeof window !== 'undefined') window.dispatchEvent(new Event(FEEDBACK_SEEN_EVENT));
    } catch (_e) { /* 표시를 못 지워도 목록은 보여야 한다 */ }
}

export const LOGS_SEEN_EVENT = 'livesql:logs-seen';
export async function markLogsSeen() {
    try {
        await putJson('/seen', {});
        if (typeof window !== 'undefined') window.dispatchEvent(new Event(LOGS_SEEN_EVENT));
    } catch (_e) { /* 표시를 못 지워도 창은 열려야 한다 */ }
}

/**
 * 스탠드얼론 진단 파일을 받아 저장한다. 서버가 SQL·메시지를 뺀 내용을 준다(opsLog/router `/diagnostics`).
 * 파일 이름은 화면이 짓는다 — 스탠드얼론은 오리진이 달라 응답 머리글을 못 읽을 수 있다.
 */
export async function downloadDiagnostics() {
    const r = await fetch(`${BASE_URL}/logs/diagnostics`, { credentials: 'include' });
    if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        const e = new Error(data.error || `HTTP ${r.status}`);
        e.errorType = data.errorType || 'REQUEST_FAILED';
        throw e;
    }
    const blob = await r.blob();
    const name = `livesql-diagnostics-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.json`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return name;
}

/** 신고 첨부 이미지 주소 — 같은 오리진이라 쿠키가 실리고, 볼 수 있는지는 서버가 판정한다. */
export const feedbackImageUrl = (id, idx) => `${BASE_URL}/logs/feedback/${encodeURIComponent(id)}/images/${Number(idx)}`;

/**
 * 문제 신고를 보낸다. 오류 번호·직전 기능·빌드·화면·브라우저를 함께 싣고 **서버가 모양을 다시 확인한다.**
 * 기록(logAction·logClientError)과 달리 **던진다** — 사람이 누른 보내기가 조용히 실패하면 안 된다.
 * 멀티파트로 보낸다: 전역 JSON 파서의 크기 상한(100kb)이 이미지를 실은 본문을 끊는다.
 */
export async function sendFeedback({ text, incidentIds = [], images = [] }) {
    const fd = new FormData();
    fd.append('text', String(text || ''));
    fd.append('incidentIds', JSON.stringify(incidentIds));
    fd.append('actions', JSON.stringify(getRecentActions()));
    const ctx = errorContext();
    for (const k of ['release', 'route', 'browser']) if (ctx[k]) fd.append(k, ctx[k]);
    for (const img of images) fd.append('images', img, img.name || 'image');
    const r = await fetch(`${BASE_URL}/logs/feedback`, { method: 'POST', credentials: 'include', body: fd });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
        const e = new Error(body.error || `HTTP ${r.status}`);
        e.errorType = body.errorType || 'REQUEST_FAILED';
        e.body = body;
        throw e;
    }
    return body;
}

/** 기록 수준 변경. **사람만 부른다** — 화면에서만 이 함수를 쓴다. */
export async function setLogLevel({ level, reason, target }) {
    const r = await fetch(`${BASE_URL}/logs/level`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ level, reason, target }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) {
        const e = new Error(body.error || `HTTP ${r.status}`);
        e.errorType = body.errorType || 'REQUEST_FAILED';
        e.body = body;
        throw e;
    }
    return body;
}

const opsLog = { logAction, logClientError, flush, installGlobalErrorLogging, CLIENT_ACTIONS };
export default opsLog;
